import type { MetroArea, Quantile } from "@lossdog/job-watchlists";
import { getQuantile } from "@lossdog/job-watchlists";
import { useMemo } from "react";

import { BizProfileQueries } from "@/data/bizProfile/bizProfile.queries";

interface UseJobTitleUserCountsProps {
  jobTitles: string[];
  selectedMetroArea: MetroArea | null;
  selectedQuantile: Quantile | null;
  enabled?: boolean;
}

interface JobTitleUserCount {
  jobTitle: string;
  userCount: number;
}

export const useJobTitleUserCounts = ({
  jobTitles,
  selectedMetroArea,
  selectedQuantile,
  enabled = true,
}: UseJobTitleUserCountsProps) => {
  // Fetch job positions data
  // Note: When backend supports location and quantile filters,
  // we can add them to the API call
  const {
    data: jobPositionsData,
    isLoading,
    error,
  } = BizProfileQueries.useListJobTitles(
    {
      limit: 100, // Large limit to get all positions
      order: "", // Order by count descending to get most relevant matches first
      filterMsaLocation: selectedMetroArea?.title,
      filterExperienceYears: {
        min: getQuantile(selectedQuantile, "min") || 0,
        max: getQuantile(selectedQuantile, "max") || 0,
      },
    },
    {
      enabled: enabled && jobTitles.length > 0,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    },
  );

  // Create a map of job title to user count
  const jobTitleUserCounts = useMemo<Map<string, number>>(() => {
    if (!jobPositionsData?.items || jobTitles.length === 0) {
      return new Map();
    }

    const countsMap = new Map<string, number>();

    // Initialize all job titles with 0 count
    jobTitles.forEach((title) => {
      countsMap.set(title, 0);
    });

    // Match job positions with job titles and accumulate counts
    jobPositionsData.items.forEach((position) => {
      // Find matching job titles using flexible matching
      const matchingTitles = jobTitles.filter((title) => {
        const titleLower = title.toLowerCase().trim();
        const positionLower = position.jobTitle.toLowerCase().trim();

        // Check for exact match first
        if (titleLower === positionLower) {
          return true;
        }

        // Check if job title contains the position or vice versa
        if (titleLower.includes(positionLower) || positionLower.includes(titleLower)) {
          return true;
        }

        // Check for word-level matches (split by spaces, hyphens, etc.)
        const titleWords = titleLower.split(/[\s\-_]+/).filter((word) => word.length > 2);
        const positionWords = positionLower.split(/[\s\-_]+/).filter((word: string) => word.length > 2);

        // If at least 2 significant words match, consider it a match
        const matchingWords = titleWords.filter((titleWord) =>
          positionWords.some(
            (positionWord: string) => titleWord.includes(positionWord) || positionWord.includes(titleWord),
          ),
        );

        return matchingWords.length >= Math.min(2, titleWords.length);
      });

      // Add count to all matching titles
      matchingTitles.forEach((matchingTitle) => {
        const currentCount = countsMap.get(matchingTitle) || 0;
        countsMap.set(matchingTitle, currentCount + position.count);
      });
    });

    return countsMap;
  }, [jobPositionsData?.items, jobTitles]);

  // Convert map to array format for easier consumption
  const jobTitleUserCountsArray = useMemo<JobTitleUserCount[]>(() => {
    return jobTitles.map((jobTitle) => ({
      jobTitle,
      userCount: jobTitleUserCounts.get(jobTitle) || 0,
    }));
  }, [jobTitles, jobTitleUserCounts]);

  return {
    jobTitleUserCounts: jobTitleUserCountsArray,
    jobTitleUserCountsMap: jobTitleUserCounts,
    isLoading,
    error,
  };
};
